import React, { memo, useEffect } from "react";
import { List, Collapse, Box } from "@mui/material";
import NavigationItem from "../../../components/NavigationItem";
import { useNavigation } from "../../../hooks/useNavigation";
import { validateNavigationItem } from "../../../constants/navigation";

/**
 * Individual expandable navigation item with children
 * Memoized for performance optimization
 */
const ExpandableListItem = memo(
  ({ item, isSubItem = false, navigationState, parentIsActive = false }) => {
    const {
      isActive,
      hasActiveChild,
      isExpanded,
      handleItemClick,
      toggleExpanded,
    } = navigationState;

    // Validate the navigation item
    if (!validateNavigationItem(item)) {
      return null;
    }

    // Check if this is the active sub-item
    const isActiveSubItem = isSubItem && isActive(item.path);

    // For parent items: check if this item is active or has active children
    const isParentActive =
      !isSubItem && (isActive(item.path) || hasActiveChild(item));

    // Check if this item should have white icons:
    // 1. If the item itself is active
    // 2. If it's a parent item that has active children
    // 3. For sub-items, if their parent is active
    const shouldHaveWhiteIcons =
      isActive(item.path) || isParentActive || (isSubItem && parentIsActive);

    const expanded = isExpanded(item);

    return (
      <React.Fragment key={item.text}>
        <NavigationItem
          item={item}
          isSubItem={isSubItem}
          isActive={isActive(item.path)}
          isParentActive={isParentActive}
          shouldHaveWhiteIcons={shouldHaveWhiteIcons}
          isExpanded={expanded}
          onItemClick={handleItemClick}
          onToggleExpanded={toggleExpanded}
        />
        {item.children && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 20,
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  backgroundColor: "#cccccc",
                },
              }}
            >
              <NestedList
                items={item.children}
                isSubItem={true}
                navigationState={navigationState}
                parentIsActive={isParentActive}
              />
            </Box>
          </Collapse>
        )}
      </React.Fragment>
    );
  }
);

ExpandableListItem.displayName = "ExpandableListItem";

/**
 * Main NestedList component
 * Handles rendering of navigation items with proper hierarchy
 */
const NestedList = memo(
  ({
    items = [],
    isSubItem = false,
    navigationState,
    parentIsActive = false,
  }) => {
    const { autoExpandActiveItems } = navigationState;

    // Auto-expand items with active children on mount
    useEffect(() => {
      if (!isSubItem && items.length > 0) {
        autoExpandActiveItems(items);
      }
    }, [items, autoExpandActiveItems, isSubItem]);

    // Filter out invalid items
    const validItems = items.filter(validateNavigationItem);

    if (validItems.length === 0) {
      return null;
    }

    return (
      <List role="menu" aria-label={isSubItem ? "Submenu" : "Main navigation"}>
        {validItems.map((item) => (
          <ExpandableListItem
            key={item.text}
            item={item}
            isSubItem={isSubItem}
            navigationState={navigationState}
            parentIsActive={parentIsActive}
          />
        ))}
      </List>
    );
  }
);

NestedList.displayName = "NestedList";

export default NestedList;
